import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCalendar } from 'react-icons/fa';
import CalendarComponent from '../../components/Calendar';
import AgendamentoModal from '../../components/AgendamentoModal';
import NotificationContainer from '../../components/NotificationContainer';
import Pagination from '../../components/Pagination';
import './AgendamentosTab.css';

const AgendamentosTab = ({
  agendamentos,
  loadingTable,
  clientes,
  imoveis,
  loadAgendamentos,
  handleSaveAgendamento,
  handleDeleteAgendamento
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingAgendamento, setEditingAgendamento] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' ou 'list'
  const [pesquisaAgendamento, setPesquisaAgendamento] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    checkNotifications();
    // Verificar notificações a cada minuto
    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendamentos]);

  const checkNotifications = () => {
    const now = new Date();
    const newNotifications = [];
    const existingIds = notifications.map(n => n.id);

    agendamentos.forEach(agendamento => {
      if (agendamento.status === 'agendado' || agendamento.status === 'confirmado') {
        if (!agendamento.dataHora) return;
        
        const dataHora = new Date(agendamento.dataHora);
        if (isNaN(dataHora.getTime())) return;
        
        const diffMs = dataHora - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        // Notificar quando faltar 7 a 1 dia(s) - uma vez por dia
        if (diffDays >= 1 && diffDays <= 7) {
          const notificationId = `agendamento-${agendamento.id}-days-${diffDays}`;
          if (!existingIds.includes(notificationId)) {
            newNotifications.push({
              id: notificationId,
              type: 'warning',
              title: `Visita Agendada - ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`,
              message: `${agendamento.clienteNome} - ${agendamento.imovelCodigo} em ${new Date(agendamento.dataHora).toLocaleString('pt-BR')}`,
              autoClose: false
            });
          }
        }

        // Notificar quando faltar 6 horas ou menos (a cada hora)
        if (diffHours >= 0 && diffHours <= 6) {
          const hourKey = Math.floor(diffHours);
          const notificationId = `agendamento-${agendamento.id}-hours-${hourKey}`;
          if (!existingIds.includes(notificationId)) {
            newNotifications.push({
              id: notificationId,
              type: diffHours <= 1 ? 'error' : 'warning',
              title: `Visita em ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`,
              message: `${agendamento.clienteNome} - ${agendamento.imovelCodigo} às ${new Date(agendamento.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
              autoClose: false,
              duration: 10000
            });
          }
        }
      }
    });

    if (newNotifications.length > 0) {
      setNotifications(prev => [...prev, ...newNotifications]);
    }
  };

  const handleCloseNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleDateClick = (date, events) => {
    setSelectedDate(date);
    setSelectedEvents(events);
  };

  const handleNewAgendamento = () => {
    setEditingAgendamento(null);
    setShowModal(true);
  };

  const handleEdit = (agendamento) => {
    setEditingAgendamento(agendamento);
    setShowModal(true);
  };

  const handleSave = async (data) => {
    try {
      await handleSaveAgendamento(data, editingAgendamento?.id);
      setShowModal(false);
      setEditingAgendamento(null);
      // loadAgendamentos já é chamado dentro de handleSaveAgendamento, mas garantimos aqui também
      await loadAgendamentos();
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      // Não mostrar alert aqui, pois já é mostrado em handleSaveAgendamento
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'agendado':
        return 'status-agendado';
      case 'confirmado':
        return 'status-confirmado';
      case 'realizado':
        return 'status-realizado';
      case 'cancelado':
        return 'status-cancelado';
      case 'remarcado':
        return 'status-remarcado';
      default:
        return '';
    }
  };

  // Filtrar apenas agendamentos futuros (data/hora >= data atual) - apenas para calendário
  const agora = new Date();
  agora.setSeconds(0, 0); // Zerar segundos e milissegundos para comparação mais precisa
  
  const agendamentosFuturos = agendamentos.filter(agendamento => {
    if (!agendamento.dataHora) return false;
    
    const dataHoraAgendamento = new Date(agendamento.dataHora);
    if (isNaN(dataHoraAgendamento.getTime())) return false;
    
    // Considerar apenas agendamentos futuros ou do mesmo dia/hora
    return dataHoraAgendamento.getTime() >= agora.getTime();
  });

  // Ordenar todos os agendamentos por data/hora (mais próximos primeiro) - para lista
  const agendamentosOrdenados = [...agendamentos].sort((a, b) => {
    if (!a.dataHora && !b.dataHora) return 0;
    if (!a.dataHora) return 1;
    if (!b.dataHora) return -1;
    
    const dataA = new Date(a.dataHora);
    const dataB = new Date(b.dataHora);
    
    if (isNaN(dataA.getTime()) && isNaN(dataB.getTime())) return 0;
    if (isNaN(dataA.getTime())) return 1;
    if (isNaN(dataB.getTime())) return -1;
    
    return dataA.getTime() - dataB.getTime();
  });

  // Ordenar agendamentos futuros para o calendário
  const agendamentosFuturosOrdenados = [...agendamentosFuturos].sort((a, b) => {
    if (!a.dataHora && !b.dataHora) return 0;
    if (!a.dataHora) return 1;
    if (!b.dataHora) return -1;
    
    const dataA = new Date(a.dataHora);
    const dataB = new Date(b.dataHora);
    
    if (isNaN(dataA.getTime()) && isNaN(dataB.getTime())) return 0;
    if (isNaN(dataA.getTime())) return 1;
    if (isNaN(dataB.getTime())) return -1;
    
    return dataA.getTime() - dataB.getTime();
  });

  // Resetar página quando pesquisa mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [pesquisaAgendamento]);

  // Filtrar agendamentos pela busca (se houver pesquisa) - para lista
  const agendamentosFiltrados = pesquisaAgendamento
    ? agendamentosOrdenados.filter(agendamento => {
        const pesquisa = pesquisaAgendamento.toLowerCase();
        const clienteNome = (agendamento.clienteNome || '').toLowerCase();
        const imovelCodigo = (agendamento.imovelCodigo || '').toLowerCase();
        const imovelTipo = (agendamento.imovelTipo || '').toLowerCase();
        const imovelBairro = (agendamento.imovelBairro || '').toLowerCase();
        const imovelCidade = (agendamento.imovelCidade || '').toLowerCase();
        const observacoes = (agendamento.observacoes || '').toLowerCase();
        const status = (agendamento.status || '').toLowerCase();
        const dataHora = agendamento.dataHora ? formatDateTime(agendamento.dataHora).toLowerCase() : '';
        
        return (
          clienteNome.includes(pesquisa) ||
          imovelCodigo.includes(pesquisa) ||
          imovelTipo.includes(pesquisa) ||
          imovelBairro.includes(pesquisa) ||
          imovelCidade.includes(pesquisa) ||
          observacoes.includes(pesquisa) ||
          status.includes(pesquisa) ||
          dataHora.includes(pesquisa)
        );
      })
    : agendamentosOrdenados; // Mostrar todos quando não houver pesquisa (para lista)

  // Paginação para lista
  const agendamentosPaginados = (() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return agendamentosFiltrados.slice(startIndex, endIndex);
  })();

  // Para calendário: mostrar os 5 mais próximos ao carregar
  const agendamentosCalendario = agendamentosFuturosOrdenados.slice(0, 5);

  return (
    <div className="dashboard-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Gerenciar Agendamentos</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className={`view-toggle ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              <FaCalendar /> Calendário
            </button>
            <button
              className={`view-toggle ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              Lista
            </button>
          </div>
          <button className="btn-add" onClick={handleNewAgendamento}>
            <FaPlus /> Nova Visita
          </button>
        </div>
      </div>

      {/* Notificações */}
      <NotificationContainer notifications={notifications} onClose={handleCloseNotification} />

      {/* Campo de busca */}
      <div style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          placeholder="Buscar agendamentos (cliente, imóvel, data, status, observações...)"
          value={pesquisaAgendamento}
          onChange={(e) => setPesquisaAgendamento(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '1px solid #ddd',
            borderRadius: '5px',
            fontSize: '0.9rem'
          }}
        />
        {!pesquisaAgendamento && agendamentosFuturos.length > 5 && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
            Mostrando os 5 agendamentos futuros mais próximos. Use a busca para ver todos.
          </div>
        )}
        {!pesquisaAgendamento && agendamentosFuturos.length === 0 && agendamentos.length > 0 && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
            Nenhum agendamento futuro encontrado.
          </div>
        )}
      </div>

      {loadingTable ? (
        <div className="loading">Carregando agendamentos...</div>
      ) : (
        <>
          {viewMode === 'calendar' ? (
            <div className="agendamentos-calendar-view">
              <CalendarComponent
                agendamentos={agendamentosFiltrados}
                onDateClick={handleDateClick}
              />
              
              {/* Sempre mostrar os 5 agendamentos mais próximos ao carregar */}
              <div className="selected-date-events">
                <h3>Próximos Agendamentos</h3>
                <div className="events-list">
                  {agendamentosCalendario.length === 0 ? (
                    <p style={{ padding: '1rem', color: '#666' }}>Nenhum agendamento futuro encontrado.</p>
                  ) : (
                    agendamentosCalendario.map(event => (
                      <div key={event.id} className="event-card">
                        <div className="event-time">
                          {new Date(event.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="event-details">
                          <h4>{event.clienteNome || '-'}</h4>
                          <p>{event.imovelCodigo} - {event.imovelTipo}</p>
                          <p className="event-location">{event.imovelBairro}, {event.imovelCidade}</p>
                          <p className="event-date">{new Date(event.dataHora).toLocaleDateString('pt-BR')}</p>
                          {event.observacoes && <p className="event-notes">{event.observacoes}</p>}
                          <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                            {event.status}
                          </span>
                        </div>
                        <div className="event-actions">
                          <button
                            onClick={() => handleEdit(event)}
                            className="btn-action btn-edit"
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Deseja realmente excluir este agendamento?')) {
                                handleDeleteAgendamento(event.id);
                              }
                            }}
                            className="btn-action btn-delete"
                            title="Excluir"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Mostrar agendamentos do dia selecionado quando clicar */}
              {selectedDate && selectedEvents.length > 0 && (
                <div className="selected-date-events" style={{ marginTop: '2rem' }}>
                  <h3>Agendamentos para {selectedDate.toLocaleDateString('pt-BR')}</h3>
                  <div className="events-list">
                    {selectedEvents.map(event => (
                      <div key={event.id} className="event-card">
                        <div className="event-time">
                          {new Date(event.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="event-details">
                          <h4>{event.clienteNome || '-'}</h4>
                          <p>{event.imovelCodigo} - {event.imovelTipo}</p>
                          <p className="event-location">{event.imovelBairro}, {event.imovelCidade}</p>
                          {event.observacoes && <p className="event-notes">{event.observacoes}</p>}
                          <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                            {event.status}
                          </span>
                        </div>
                        <div className="event-actions">
                          <button
                            onClick={() => handleEdit(event)}
                            className="btn-action btn-edit"
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Deseja realmente excluir este agendamento?')) {
                                handleDeleteAgendamento(event.id);
                              }
                            }}
                            className="btn-action btn-delete"
                            title="Excluir"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="imoveis-table">
              <table>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Imóvel</th>
                    <th>Data e Hora</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {agendamentosPaginados.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data">
                        {pesquisaAgendamento ? 'Nenhum agendamento encontrado' : 'Nenhum agendamento cadastrado'}
                      </td>
                    </tr>
                  ) : (
                    agendamentosPaginados.map(agendamento => (
                      <tr key={agendamento.id}>
                        <td>{agendamento.clienteNome || '-'}</td>
                        <td>
                          {agendamento.imovelCodigo || '-'} - {agendamento.imovelTipo || ''}
                          <br />
                          <small>{agendamento.imovelBairro}, {agendamento.imovelCidade}</small>
                        </td>
                        <td>{agendamento.dataHora ? formatDateTime(agendamento.dataHora) : '-'}</td>
                        <td>
                          <span className={`status ${getStatusBadgeClass(agendamento.status)}`}>
                            {agendamento.status}
                          </span>
                        </td>
                        <td>
                          <div className="actions">
                            <button
                              onClick={() => handleEdit(agendamento)}
                              className="btn-action btn-edit"
                              title="Editar"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Deseja realmente excluir este agendamento?')) {
                                  handleDeleteAgendamento(agendamento.id);
                                }
                              }}
                              className="btn-action btn-delete"
                              title="Excluir"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              {agendamentosFiltrados.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={agendamentosFiltrados.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Modal de Agendamento */}
      <AgendamentoModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingAgendamento(null);
        }}
        onSave={handleSave}
        agendamento={editingAgendamento}
        clientes={clientes}
        imoveis={imoveis}
      />
    </div>
  );
};

export default AgendamentosTab;

